import React, { useState } from "react";
import { graphql } from "gatsby";
import { Box, Button, Hidden, Typography, useTheme } from "@material-ui/core";
import { formatMultilinesText } from "../../helpers/markdown";
import LocalizedLink from "../../components/localized-link";
import StoryModal from "../../components/story-modal";
import FadeInUp from "../../components/animations/FadeInUp";
import { useInView } from "react-intersection-observer";
import fastCompare from "react-fast-compare";

type Props = Queries.TitleSectionFragmentFragment;

function TitleSection({
  title,
  titleSize,
  titleMobile,
  titleSizeMobile,
  titleColor,
  titleMaxWidth,
  titleTextAlign,
  subtitle,
  subtitleSize,
  subtitleMaxWidth,
  cta,
  ctaLink,
  ctaMinWidth,
  animation,
  hideDesktop,
  hideMobile
}: Props) {
  const theme = useTheme();
  const [ref, inView] = useInView({
    threshold: 0.25,
    triggerOnce: true,
    skip: !animation,
    initialInView: false
  });

  const disableAnimation = !animation || animation === "none";

  const convertDatoCmsColorToRealColor = (color: string) => {
    if (color === "white") {
      return theme.palette.common.white;
    }
    if (color === "black") {
      return theme.palette.common.black;
    }
    if (color?.includes("grey")) {
      const matches = color.match(/\[(.*?)\]/);
      if (matches) {
        return theme.palette.grey[matches[1]];
      }
    }
    return null;
  };

  const hasTitleMobileOverride = titleMobile || titleSizeMobile;

  const titleStyle = {
    color: convertDatoCmsColorToRealColor(titleColor),
    maxWidth: `min(${titleMaxWidth || 640}px, calc(100vw - 64px))`
  };

  const handleCtaStoryClick = () => {
    const story = ctaLink as Queries.DatoCmsStory;

    setStoryModalOpen(true);
  };

  const isLinkToStory = ctaLink?.model?.apiKey === "story";
  const [isStoryModalOpen, setStoryModalOpen] = useState(false);

  return (
    <>
      <Box
        textAlign={titleTextAlign === "left" ? "left" : "center"}
        flexDirection="column"
        alignItems={titleTextAlign === "left" ? "flex-start" : "center"}
        ref={ref}
        display={{
          xs: hideMobile ? "none" : "flex",
          sm: hideDesktop ? "none" : "flex"
        }}
      >
        <Box marginBottom={subtitle ? { xs: 4, md: 6 } : 0}>
          {title && !hasTitleMobileOverride && (
            <FadeInUp disabled={disableAnimation} overrideInView={inView}>
              <Typography
                variant={(titleSize as any) || "h4"}
                dangerouslySetInnerHTML={{
                  __html: formatMultilinesText(title)
                }}
                style={titleStyle}
              />
            </FadeInUp>
          )}

          {/* Title (W/ mobile override) */}
          {hasTitleMobileOverride && (
            <>
              {/*  Desktop size */}
              <Hidden xsDown implementation="css">
                <FadeInUp disabled={disableAnimation} overrideInView={inView}>
                  <Typography
                    variant={(titleSize as any) || "h4"}
                    dangerouslySetInnerHTML={{
                      __html: formatMultilinesText(title)
                    }}
                    style={titleStyle}
                  />
                </FadeInUp>
              </Hidden>

              {/*  Mobile size */}
              <Hidden smUp implementation="css">
                <FadeInUp disabled={disableAnimation} overrideInView={inView}>
                  <Typography
                    variant={((titleSizeMobile || titleSize) as any) || "h4"}
                    dangerouslySetInnerHTML={{
                      __html: formatMultilinesText(titleMobile || title)
                    }}
                    style={titleStyle}
                  />
                </FadeInUp>
              </Hidden>
            </>
          )}
        </Box>

        {subtitle && (
          <Box
            maxWidth={`${subtitleMaxWidth || 640}px`}
            marginBottom={cta && ctaLink ? { xs: 4, md: 6 } : 0}
          >
            <FadeInUp disabled={disableAnimation} overrideInView={inView}>
              <Typography
                variant={(subtitleSize as any) || "body2"}
                dangerouslySetInnerHTML={{
                  __html: formatMultilinesText(subtitle)
                }}
              />
            </FadeInUp>
          </Box>
        )}

        {cta && ctaLink && (
          <Box
            minWidth={{
              xs: "100%",
              md: ctaMinWidth ? `${ctaMinWidth}px` : undefined
            }}
            marginTop={!subtitle ? { xs: 2, md: 3 } : 0}
          >
            <FadeInUp
              disabled={disableAnimation}
              delay={animation === "fade-in-up-per-line" ? 0.75 : undefined}
              overrideInView={inView}
            >
              {!isLinkToStory && (
                <Button
                  variant="contained"
                  component={LocalizedLink}
                  to={(ctaLink as any).url || `/${(ctaLink as any).path}`}
                >
                  {cta}
                </Button>
              )}
              {isLinkToStory && (
                <Button variant="contained" onClick={handleCtaStoryClick}>
                  {cta}
                </Button>
              )}
            </FadeInUp>
          </Box>
        )}
      </Box>

      {isLinkToStory && (
        <StoryModal
          open={isStoryModalOpen}
          onClose={() => setStoryModalOpen(false)}
          story={ctaLink as Queries.DatoCmsStory}
        />
      )}
    </>
  );
}
export default React.memo(TitleSection, fastCompare);

export const TitleSectionFragment = graphql`
  fragment TitleSectionFragment on DatoCmsCModuleTitleSection {
    title
    titleSize
    titleMobile
    titleSizeMobile
    titleColor
    titleMaxWidth
    titleTextAlign
    subtitle
    subtitleSize
    subtitleMaxWidth
    cta
    ctaLink {
      ... on DatoCmsWebPage {
        path
        model {
          apiKey
        }
      }
      ... on DatoCmsStory {
        ...StoryFragment
      }
      ... on DatoCmsCardExternalLink {
        url
        model {
          apiKey
        }
      }
    }
    ctaMinWidth
    animation
    hideDesktop
    hideMobile
    container
    innerContainer
    containerDisableGutters
    containerBackgroundColor
    visibilitySettings {
      ...ContentModuleVisibilitySettingFragment
    }
    model {
      apiKey
    }
  }
`;
``;
